import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  ApplyTimePermissionInput,
  TimePermissionApplyCatalog,
  TimePermissionDashboardData,
  TimePermissionHistoryResult,
  TimePermissionOrgConfiguration,
  TimePermissionRequestDetails,
  TimePermissionRequestFilters,
  TimePermissionRequestListItem,
  TimePermissionType,
} from '../types/timePermission';
import { calendarDateFromApi } from '../utils/dateTime';
import {
  createDefaultTimePermissionFilters,
  normalizeTimePermissionFilters,
} from '../utils/timePermissionFilters';
import {
  emptyTimePermissionOrgConfiguration,
  isPermissionOrgConfigured,
} from '../utils/timePermissionApply';
import { resolveRequestCanCancel } from '../utils/requestCancelWindow';
import { getTimePermissionStatusLabel } from '../utils/timePermissionStatus';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function toCamelKey(key: string): string {
  return key.replace(/_([a-z0-9])/gi, (_, ch: string) => ch.toUpperCase());
}

function camelizeRow(row: ObjectValue): ObjectValue {
  const flattened: ObjectValue = { ...row };
  for (const [key, value] of Object.entries(row)) {
    flattened[toCamelKey(key)] = value;
  }
  return flattened;
}

function objectRow(value: unknown): ObjectValue | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as ObjectValue;
}

function detailsRow(payload: ObjectValue): ObjectValue {
  const nestedRequest = objectRow(payload.request);
  const nestedWorkflow = objectRow(payload.workflow);
  const merged = camelizeRow({
    ...payload,
    ...(nestedWorkflow ?? {}),
    ...(nestedRequest ?? {}),
  });
  const cancellationReason =
    textOrNull(
      nestedRequest?.cancellationReason ??
        nestedRequest?.cancellation_reason ??
        nestedRequest?.cancelReason ??
        nestedRequest?.cancel_reason,
    ) ??
    textOrNull(
      payload.cancellationReason ??
        payload.cancellation_reason ??
        payload.cancelReason ??
        payload.cancel_reason,
    ) ??
    textOrNull(
      nestedWorkflow?.cancellationReason ??
        nestedWorkflow?.cancellation_reason ??
        nestedWorkflow?.cancelReason ??
        nestedWorkflow?.cancel_reason,
    );
  if (cancellationReason) {
    merged.cancellationReason = cancellationReason;
  }
  return merged;
}

function dateOnly(value: unknown): string | null {
  return calendarDateFromApi(value);
}

function timeOnly(value: unknown): string | null {
  const text = textOrNull(value);
  if (!text) return null;
  const match = text.match(/(\d{2}:\d{2})(?::\d{2})?/);
  return match?.[1] ?? null;
}

function reasonSummary(value: unknown): string | null {
  const text = textOrNull(value);
  if (!text) return null;
  return text.length > 80 ? `${text.slice(0, 77)}...` : text;
}

function booleanOrUndefined(value: unknown): boolean | undefined {
  if (value === true || value === 1 || value === '1' || value === 'true') {
    return true;
  }
  if (value === false || value === 0 || value === '0' || value === 'false') {
    return false;
  }
  return undefined;
}

function isRequestCancellable(statusCode: string | null, row: ObjectValue): boolean {
  const permissionDate = dateOnly(
    row.permissionDate ?? row.requestDate ?? row.fromDate,
  );
  return resolveRequestCanCancel({
    statusCode,
    canCancelFromApi: booleanOrUndefined(
      row.canCancel ?? row.can_cancel ?? row.allowCancel ?? row.allow_cancel,
    ),
    fromDate: permissionDate,
    toDate: permissionDate,
  });
}

function normalizeRequest(
  row: ObjectValue,
): TimePermissionRequestListItem | null {
  const permissionRequestId = numberOrNull(
    row.permissionRequestId ?? row.requestId,
  );
  if (permissionRequestId === null || permissionRequestId <= 0) return null;

  const statusCode =
    textOrNull(row.statusCode) ?? textOrNull(row.status) ?? null;

  return {
    permissionRequestId,
    requestNumber: textOrNull(
      row.requestNumber ?? row.permissionRequestNumber,
    ),
    permissionDate: dateOnly(
      row.permissionDate ?? row.requestDate ?? row.fromDate,
    ),
    fromTime: timeOnly(row.fromTime ?? row.startTime),
    toTime: timeOnly(row.toTime ?? row.endTime),
    durationMinutes: numberOrNull(
      row.durationMinutes ??
        row.totalMinutes ??
        row.permissionMinutes ??
        row.approvedMinutes,
    ),
    permissionTypeCode: textOrNull(
      row.permissionTypeCode ?? row.typeCode,
    ),
    permissionTypeName: textOrNull(
      row.permissionTypeName ?? row.typeName ?? row.permissionType,
    ),
    statusCode,
    statusLabel: getTimePermissionStatusLabel(statusCode),
    applicationDate: dateOnly(
      row.applicationDate ?? row.appliedOn ?? row.createdAt,
    ),
    reasonSummary: reasonSummary(
      row.reason ?? row.permissionReason ?? row.remarks,
    ),
    canCancel: isRequestCancellable(statusCode, row),
  };
}

function normalizeDetails(row: ObjectValue): TimePermissionRequestDetails | null {
  const base = normalizeRequest(row);
  if (!base) return null;

  const workflowInstanceId = numberOrNull(
    row.workflowInstanceId ?? row.workflowId,
  );
  const currentStepName = textOrNull(
    row.currentStepName ?? row.workflowStepName,
  );
  const currentApproverName = textOrNull(
    row.currentApproverName ?? row.approverName,
  );
  const hasWorkflow =
    workflowInstanceId !== null ||
    currentStepName !== null ||
    currentApproverName !== null;

  return {
    permissionRequestId: base.permissionRequestId,
    requestNumber: base.requestNumber,
    applicationDate: base.applicationDate,
    permissionDate: base.permissionDate,
    fromTime: base.fromTime,
    toTime: base.toTime,
    durationMinutes: base.durationMinutes,
    permissionType: {
      permissionTypeId: numberOrNull(
        row.permissionTypeId ?? row.typeId,
      ),
      permissionTypeCode: base.permissionTypeCode,
      permissionTypeName: base.permissionTypeName,
    },
    statusCode: base.statusCode,
    statusLabel: base.statusLabel,
    reason: textOrNull(row.reason ?? row.permissionReason),
    contactInformation: textOrNull(
      row.contactInformation ?? row.contactNumber ?? row.contact,
    ),
    remarks: textOrNull(row.remarks ?? row.notes),
    submittedAt: textOrNull(
      row.submittedAt ?? row.appliedAt ?? row.appliedOn ?? row.createdAt,
    ),
    rejectReason: textOrNull(
      row.rejectReason ?? row.rejectionReason ?? row.rejectedReason,
    ),
    cancellationReason: textOrNull(
      row.cancellationReason ??
        row.cancelReason ??
        row.cancelledReason ??
        row.canceledReason ??
        row.cancellation_reason ??
        row.cancel_reason,
    ),
    employee: {
      employeeId: numberOrNull(
        row.employeeId ?? row.requesterEmployeeId ?? row.applicantEmployeeId,
      ),
      displayName: textOrNull(
        row.displayName ??
          row.requesterName ??
          row.employeeName ??
          row.requesterDisplayName,
      ),
      employeeNumber: textOrNull(
        row.employeeNumber ?? row.requesterEmployeeNumber,
      ),
      designationName: textOrNull(
        row.designationName ?? row.requesterDesignationName,
      ),
    },
    workflow: hasWorkflow
      ? {
          workflowInstanceId,
          currentStepName,
          currentApproverName,
        }
      : null,
    canCancel: base.canCancel,
  };
}

export async function getTimePermissionDashboard(options?: {
  fromDate?: string;
  toDate?: string;
}): Promise<TimePermissionDashboardData> {
  const params: Record<string, string> = {};
  if (options?.fromDate) params.fromDate = options.fromDate;
  if (options?.toDate) params.toDate = options.toDate;

  const response = await apiClient.get<
    Envelope<{
      statistics?: ObjectValue;
      recentRequests?: ObjectValue[];
    }>
  >(API_ENDPOINTS.TIME_PERMISSION.DASHBOARD, {
    params: Object.keys(params).length ? params : undefined,
  });

  const data = response.data.data ?? {};
  const statistics = data.statistics ?? {};
  const recent = Array.isArray(data.recentRequests)
    ? data.recentRequests
        .map(normalizeRequest)
        .filter((item): item is TimePermissionRequestListItem => item !== null)
    : [];

  return {
    statistics: {
      totalRequests: numberOrNull(
        statistics.totalRequests ?? statistics.totalPermissionRequests,
      ),
      pendingRequests: numberOrNull(
        statistics.pendingRequests ?? statistics.pendingCount,
      ),
      approvedMinutes: numberOrNull(
        statistics.approvedMinutes ?? statistics.totalApprovedMinutes,
      ),
    },
    recentRequests: recent,
  };
}

export async function getTimePermissionRequests(
  options: {
    filters?: Partial<TimePermissionRequestFilters>;
    page?: number;
    pageSize?: number;
  } = {},
): Promise<TimePermissionHistoryResult> {
  const defaults = createDefaultTimePermissionFilters();
  const normalized = normalizeTimePermissionFilters({
    fromDate: options.filters?.fromDate ?? defaults.fromDate,
    toDate: options.filters?.toDate ?? defaults.toDate,
    page: options.page ?? 1,
    pageSize: options.pageSize ?? 20,
  });

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: unknown;
        pageSize?: unknown;
        totalRecords?: unknown;
        totalPages?: unknown;
      };
    }>
  >(API_ENDPOINTS.TIME_PERMISSION.HISTORY, {
    params: {
      fromDate: normalized.filters.fromDate,
      toDate: normalized.filters.toDate,
      page: normalized.page,
      pageSize: normalized.pageSize,
    },
  });

  const data = response.data.data ?? {};
  const pagination = data.pagination ?? {};
  const items = Array.isArray(data.items)
    ? data.items
        .map(normalizeRequest)
        .filter((item): item is TimePermissionRequestListItem => item !== null)
    : [];
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const pageSize = numberOrNull(pagination.pageSize) ?? normalized.pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / Math.max(pageSize, 1)));

  return {
    items,
    page: numberOrNull(pagination.page) ?? normalized.page,
    pageSize,
    totalItems,
    totalPages,
  };
}

export async function getTimePermissionRequestDetails(
  permissionRequestId: number,
): Promise<TimePermissionRequestDetails> {
  if (!Number.isInteger(permissionRequestId) || permissionRequestId <= 0) {
    throw Object.assign(
      new Error('permissionRequestId must be a positive integer'),
      { code: 'VALIDATION_ERROR' },
    );
  }

  const response = await apiClient.get<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.TIME_PERMISSION.REQUEST_DETAILS(permissionRequestId));

  const payload = response.data.data ?? {};
  const details = normalizeDetails(detailsRow((payload ?? {}) as ObjectValue));
  if (!details) {
    throw Object.assign(new Error('Permission request was not found.'), {
      code: 'PERMISSION_REQUEST_NOT_FOUND',
    });
  }
  return details;
}

function isActiveFlag(value: unknown): boolean {
  if (value === false || value === 0 || value === '0' || value === 'false') {
    return false;
  }
  return true;
}

function isDefaultFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}

function normalizeOrgConfiguration(
  row: ObjectValue | null | undefined,
): TimePermissionOrgConfiguration | null {
  if (!row) return null;
  const configuration: TimePermissionOrgConfiguration = {
    maximumPermissionsPerMonth: numberOrNull(row.maximumPermissionsPerMonth) ?? 0,
    minimumDurationMinutes: numberOrNull(row.minimumDurationMinutes) ?? 0,
    maximumDurationMinutes: numberOrNull(row.maximumDurationMinutes) ?? 0,
    usedMonth: numberOrNull(row.usedMonth) ?? 0,
    remainingMonth: numberOrNull(row.remainingMonth) ?? 0,
  };
  return isPermissionOrgConfigured(configuration) ? configuration : {
    ...emptyTimePermissionOrgConfiguration(),
    ...configuration,
  };
}

export async function getTimePermissionApplyCatalog(
  permissionDate?: string,
): Promise<TimePermissionApplyCatalog> {
  const response = await apiClient.get<
    Envelope<{ items?: ObjectValue[]; configuration?: ObjectValue } | ObjectValue[]>
  >(API_ENDPOINTS.TIME_PERMISSION.TYPES, {
    params: permissionDate ? { permissionDate } : undefined,
  });

  const payload = response.data.data;
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
      ? payload.items
      : [];
  const configuration = Array.isArray(payload)
    ? null
    : normalizeOrgConfiguration(objectRow(payload?.configuration));
  const ranked = [...rows].sort((left, right) => {
    const defaultDelta =
      Number(isDefaultFlag(right.isDefault)) -
      Number(isDefaultFlag(left.isDefault));
    if (defaultDelta !== 0) return defaultDelta;
    const leftName = textOrNull(left.permissionTypeName) ?? '';
    const rightName = textOrNull(right.permissionTypeName) ?? '';
    return leftName.localeCompare(rightName);
  });

  const seen = new Set<string>();
  const types: TimePermissionType[] = [];
  for (const row of ranked) {
    if (!isActiveFlag(row.isActive)) continue;
    const permissionTypeCode = textOrNull(row.permissionTypeCode)?.toUpperCase();
    const permissionTypeId = numberOrNull(row.permissionTypeId);
    if (!permissionTypeCode || permissionTypeId === null || permissionTypeId <= 0) {
      continue;
    }
    if (seen.has(permissionTypeCode)) continue;
    seen.add(permissionTypeCode);
    types.push({
      permissionTypeId,
      permissionTypeCode,
      permissionTypeName:
        textOrNull(row.permissionTypeName) ?? permissionTypeCode,
      minimumMinutes:
        configuration?.minimumDurationMinutes ??
        numberOrNull(row.minimumMinutes ?? row.minimumDurationMinutes),
      maximumMinutes:
        configuration?.maximumDurationMinutes ??
        numberOrNull(row.maximumMinutes ?? row.maximumDurationMinutes),
      reasonRequired: true,
      advanceNoticeRequired: false,
    });
  }
  return { types, configuration };
}

export async function getTimePermissionTypes(): Promise<TimePermissionType[]> {
  return (await getTimePermissionApplyCatalog()).types;
}

export async function applyTimePermissionRequest(
  input: ApplyTimePermissionInput,
): Promise<TimePermissionRequestDetails> {
  const reason = textOrNull(input.reason);
  const permissionType = textOrNull(input.permissionType)?.toUpperCase();
  if (!reason) {
    throw Object.assign(new Error('reason is required'), {
      code: 'VALIDATION_ERROR',
    });
  }
  if (!permissionType) {
    throw Object.assign(new Error('permissionType is required'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.TIME_PERMISSION.APPLY, {
    permissionDate: input.permissionDate,
    permissionType,
    startTime: input.startTime,
    endTime: input.endTime,
    reason,
  });

  const payload = response.data.data ?? {};
  const row =
    payload &&
    typeof payload === 'object' &&
    'request' in payload &&
    payload.request &&
    typeof payload.request === 'object'
      ? (payload.request as ObjectValue)
      : (payload as ObjectValue);

  const details = normalizeDetails(row);
  if (!details) {
    throw Object.assign(new Error('Permission request could not be created.'), {
      code: 'PERMISSION_APPLY_FAILED',
    });
  }
  return details;
}

export async function cancelTimePermissionRequest(
  permissionRequestId: number,
  reason: string,
): Promise<TimePermissionRequestDetails> {
  if (!Number.isInteger(permissionRequestId) || permissionRequestId <= 0) {
    throw Object.assign(
      new Error('permissionRequestId must be a positive integer'),
      { code: 'VALIDATION_ERROR' },
    );
  }
  const cancellationReason = textOrNull(reason);
  if (!cancellationReason) {
    throw Object.assign(new Error('cancellationReason is required'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.TIME_PERMISSION.CANCEL(permissionRequestId), {
    cancellationReason,
    reason: cancellationReason,
  });

  const payload = response.data.data ?? {};
  const details = normalizeDetails(detailsRow((payload ?? {}) as ObjectValue));
  if (!details) {
    throw Object.assign(new Error('Permission request was not found.'), {
      code: 'PERMISSION_REQUEST_NOT_FOUND',
    });
  }
  return details;
}

export async function approveTimePermissionRequest(
  permissionRequestId: number,
): Promise<TimePermissionRequestDetails> {
  if (!Number.isInteger(permissionRequestId) || permissionRequestId <= 0) {
    throw Object.assign(
      new Error('permissionRequestId must be a positive integer'),
      { code: 'VALIDATION_ERROR' },
    );
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.TIME_PERMISSION.APPROVE(permissionRequestId));

  const payload = response.data.data ?? {};
  const row =
    payload &&
    typeof payload === 'object' &&
    'request' in payload &&
    payload.request &&
    typeof payload.request === 'object'
      ? (payload.request as ObjectValue)
      : (payload as ObjectValue);

  const details = normalizeDetails(row);
  if (!details) {
    throw Object.assign(new Error('Permission request was not found.'), {
      code: 'PERMISSION_REQUEST_NOT_FOUND',
    });
  }
  return details;
}

export async function rejectTimePermissionRequest(
  permissionRequestId: number,
  reason: string,
): Promise<TimePermissionRequestDetails> {
  if (!Number.isInteger(permissionRequestId) || permissionRequestId <= 0) {
    throw Object.assign(
      new Error('permissionRequestId must be a positive integer'),
      { code: 'VALIDATION_ERROR' },
    );
  }
  const rejectionReason = textOrNull(reason);
  if (!rejectionReason) {
    throw Object.assign(new Error('rejectionReason is required'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.TIME_PERMISSION.REJECT(permissionRequestId), {
    rejectionReason,
  });

  const payload = response.data.data ?? {};
  const row =
    payload &&
    typeof payload === 'object' &&
    'request' in payload &&
    payload.request &&
    typeof payload.request === 'object'
      ? (payload.request as ObjectValue)
      : (payload as ObjectValue);

  const details = normalizeDetails(row);
  if (!details) {
    throw Object.assign(new Error('Permission request was not found.'), {
      code: 'PERMISSION_REQUEST_NOT_FOUND',
    });
  }
  return details;
}
